import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Copy, Check } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { cleanUserFacingText } from '@/lib/userFacingText'

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="code-block-wrapper">
      <div className="code-block-header">
        <span className="code-block-lang">{language}</span>
        <button
          className="code-block-copy"
          onClick={() => {
            navigator.clipboard.writeText(code)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          }}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? 'Kopiert' : 'Kopier'}</span>
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          padding: '14px 16px',
          fontSize: '0.82rem',
          lineHeight: 1.55,
          background: '#0D0D0D',
        }}
        wrapLongLines
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

const components: Components = {
  a({ href, children }: { href?: string; children?: ReactNode }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="prose-link">
        {children}
      </a>
    )
  },
  h1({ children }: { children?: ReactNode }) {
    return <h1 className="sine-md-h1">{children}</h1>
  },
  h2({ children }: { children?: ReactNode }) {
    return <h2 className="sine-md-h2">{children}</h2>
  },
  h3({ children }: { children?: ReactNode }) {
    return <h3 className="sine-md-h3">{children}</h3>
  },
  p({ children }: { children?: ReactNode }) {
    return <p className="sine-md-p">{children}</p>
  },
  strong({ children }: { children?: ReactNode }) {
    return <strong className="sine-md-strong">{children}</strong>
  },
  code({ className, children }: { className?: string; children?: ReactNode }) {
    const match = /language-(\w+)/.exec(className || '')
    if (!match) return <code className="prose-inline-code">{children}</code>
    return <CodeBlock language={match[1]} code={String(children).replace(/\n$/, '')} />
  },
  ul({ children }: { children?: ReactNode }) {
    return <ul className="sine-md-list">{children}</ul>
  },
  ol({ children }: { children?: ReactNode }) {
    return <ol className="sine-md-list">{children}</ol>
  },
  li({ children }: { children?: ReactNode }) {
    return <li className="sine-md-li">{children}</li>
  },
  hr() {
    return <hr className="sine-md-hr" />
  },
  table({ children }: { children?: ReactNode }) {
    return (
      <div className="sine-md-table-wrap">
        <table className="sine-md-table">{children}</table>
      </div>
    )
  },
  th({ children }: { children?: ReactNode }) {
    return <th className="sine-md-th">{children}</th>
  },
  td({ children }: { children?: ReactNode }) {
    return <td className="sine-md-td">{children}</td>
  },
}

export function SineMarkdown({ children }: { children: string }) {
  const cleanChildren = cleanUserFacingText(children)

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {cleanChildren}
    </ReactMarkdown>
  )
}
